import { useMemo, useState } from 'react';
import { createFileRoute, Link, Outlet, redirect, useNavigate } from '@tanstack/react-router';
import { MapPin, Search, Wrench } from 'lucide-react';
import { useAuthStore } from '@/stores/auth.store';
import { Card, CardContent } from '@/components/atoms/card';
import { Badge } from '@/components/atoms/badge';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Skeleton } from '@/components/atoms/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/atoms/table';
import { useLocations } from '@/hooks/locations/useLocations';
import { useEntityRouteState } from '@/hooks/navigation/useEntityRouteState';
import { locationDisplayName, type Location } from '@/types/location';

export const Route = createFileRoute('/_dashboard/dashboard/locations')({
  beforeLoad: () => {
    const { permissions } = useAuthStore.getState();
    // Locations reuse the contacts scope — a location is customer data.
    if (permissions.length > 0 && !permissions.includes('contacts:view')) {
      throw redirect({ to: '/dashboard/home' });
    }
  },
  component: LocationsPage,
});

const PAGE_SIZE = 50;

const ROLE_SHORT: Record<string, string> = {
  SERVICE: 'Service',
  BILLING: 'Billing',
  APPROVER: 'Approver',
  OTHER: 'Other',
};

const personName = (c: {
  displayName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  companyName?: string | null;
}): string =>
  c.displayName?.trim() ||
  [c.firstName, c.lastName].filter(Boolean).join(' ').trim() ||
  c.companyName?.trim() ||
  'Unnamed contact';

/**
 * Who is at the site, as opposed to who owns it.
 *
 * Both are rows in `contacts`, so the column has to do the distinguishing:
 * the owner sits in its own column, and these are labelled by the role they
 * hold HERE. A site that names nobody says so, because "falls back to the
 * account" is a fact about the place, not an empty cell.
 */
function SitePeople({
  people,
  ownerId,
}: {
  people: NonNullable<Location['contacts']>;
  ownerId: string;
}) {
  // A person who is both the service and the billing contact is TWO rows in
  // the data — one per role, so their switches can differ — but one human.
  // Listing the rows verbatim printed their name twice.
  const byPerson = new Map<string, { name: string; roles: string[]; muted: boolean }>();
  for (const person of people) {
    const existing = byPerson.get(person.contactId);
    const role = ROLE_SHORT[person.role] ?? person.role;
    if (existing) {
      if (!existing.roles.includes(role)) existing.roles.push(role);
      // Muted only when EVERY role they hold here is muted: one live role
      // still reaches them.
      existing.muted = existing.muted && !person.receivesNotifications;
      continue;
    }
    byPerson.set(person.contactId, {
      name: personName(person.contact),
      roles: [role],
      muted: !person.receivesNotifications,
    });
  }
  const rows = [...byPerson.entries()].sort(
    ([a], [b]) => Number(b === ownerId) - Number(a === ownerId),
  );

  if (rows.length === 0) {
    return <span className="text-muted-foreground text-xs italic">Account contact only</span>;
  }
  return (
    <div className="space-y-2">
      {rows.slice(0, 3).map(([id, row]) => (
        <div key={id} className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-sm font-medium">{row.name}</span>
          <span className="text-xs text-muted-foreground">{row.roles.join(' · ')}</span>
        </div>
      ))}
      {rows.length > 3 ? (
        <span className="text-xs text-muted-foreground">+{rows.length - 3} more</span>
      ) : null}
    </div>
  );
}

function LocationsPage() {
  const navigate = useNavigate();
  // This file is the LAYOUT for `locations/$locationId.tsx`, not a sibling of
  // it. Without an Outlet the detail route mounts nothing: the URL changes and
  // the list just stays on screen. Typecheck, lint and the unit suite all pass
  // in that state — only loading the page in a browser shows it. Mirrors the
  // contacts route, which solves the same problem the same way.
  const { hasChildRoute } = useEntityRouteState({
    basePath: '/dashboard/locations',
    search: {},
  });
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);

  const params = useMemo(
    () => ({
      search: search || undefined,
      includeContacts: true,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [search, page],
  );
  const { data, isLoading, isError } = useLocations(params);

  const rows = data?.data ?? [];
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  function applySearch(value: string) {
    setSearch(value);
    // A filtered result set is shorter, so a held page offset can land past the
    // end and render an empty table that looks like "no locations".
    setPage(0);
  }

  if (hasChildRoute) return <Outlet />;

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            <MapPin className="h-6 w-6" />
            Locations
          </h1>
          <p className="text-muted-foreground text-sm">
            Every place you do work. Jobs, estimates and equipment belong to a location; the
            customer is reached through it.
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <div className="relative max-w-md flex-1">
          <Search className="text-muted-foreground absolute left-2 top-2.5 h-4 w-4" />
          <Input
            className="pl-8"
            placeholder="Search by number, nickname, street, city or customer…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') applySearch(searchInput);
            }}
          />
        </div>
        <Button variant="secondary" onClick={() => applySearch(searchInput)}>
          Search
        </Button>
        {search ? (
          <Button
            variant="ghost"
            onClick={() => {
              setSearchInput('');
              applySearch('');
            }}
          >
            Clear
          </Button>
        ) : null}
      </div>

      <Card className="overflow-hidden py-0 shadow-none">
        <CardContent className="p-0">
          <Table className="min-w-[1050px] [&_td]:px-5 [&_td]:py-5 [&_th]:px-5 [&_th]:font-sans [&_th]:text-xs [&_th]:tracking-normal [&_th]:normal-case">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[22%]">Location</TableHead>
                <TableHead>Address</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead className="min-w-[230px]">People</TableHead>
                <TableHead className="w-[230px]">Type</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={5}>
                      <Skeleton className="h-5 w-full" />
                    </TableCell>
                  </TableRow>
                ))
              ) : isError ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-muted-foreground py-10 text-center">
                    Could not load locations.
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-muted-foreground py-10 text-center">
                    {search
                      ? `No locations match “${search}”.`
                      : 'No locations yet. They are created from a customer’s addresses.'}
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((location: Location) => (
                  <TableRow
                    key={location.id}
                    className="cursor-pointer"
                    onClick={() =>
                      void navigate({
                        to: '/dashboard/locations/$locationId',
                        params: { locationId: location.id },
                      })
                    }
                  >
                    <TableCell className="font-medium whitespace-normal wrap-anywhere">
                      <div className="flex items-center gap-3">
                        <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                          <MapPin className="size-4" />
                        </span>
                        <div className="min-w-0 space-y-1">
                          <Link
                            to="/dashboard/locations/$locationId"
                            params={{ locationId: location.id }}
                            onClick={(event) => event.stopPropagation()}
                            className="font-medium text-foreground hover:underline"
                          >
                            {locationDisplayName(location)}
                          </Link>
                          <p className="font-mono text-xs text-muted-foreground">
                            {location.locationNumber}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground whitespace-normal wrap-anywhere">
                      <div className="space-y-1">
                        <p className="text-foreground">
                          {[location.street1, location.street2].filter(Boolean).join(' ')}
                        </p>
                        <p className="text-xs">
                          {[
                            location.city,
                            [location.state, location.postalCode].filter(Boolean).join(' '),
                          ]
                            .filter(Boolean)
                            .join(', ')}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell className="whitespace-normal wrap-anywhere">
                      {/* The account that owns the place — who the invoice is
                          addressed to, and who we fall back to when the site
                          names nobody. Never one of the people beside it. */}
                      {location.customer ? (
                        <Link
                          to="/dashboard/contacts/$contactId"
                          params={{ contactId: location.customerId }}
                          // The row navigates to the location; this cell is a
                          // door to the customer, so it must not do both.
                          onClick={(event) => event.stopPropagation()}
                          className="text-primary hover:underline"
                        >
                          {location.customer.companyName || location.customer.displayName}
                        </Link>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell className="whitespace-normal wrap-anywhere">
                      <SitePeople people={location.contacts ?? []} ownerId={location.customerId} />
                    </TableCell>
                    <TableCell>
                      <div className="flex w-max flex-nowrap items-center gap-1.5">
                        {location.isPrimary ? <Badge variant="outline">Primary</Badge> : null}
                        {location.isService ? (
                          <Badge variant="secondary">
                            <Wrench className="mr-1 h-3 w-3" />
                            Service
                          </Badge>
                        ) : null}
                        {location.isBilling ? <Badge variant="outline">Billing</Badge> : null}
                        {location.status === 'INACTIVE' ? (
                          <Badge variant="destructive">Inactive</Badge>
                        ) : null}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {total > PAGE_SIZE ? (
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground text-sm">
            {total} location{total === 1 ? '' : 's'} · page {page + 1} of {pageCount}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page + 1 >= pageCount}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
